import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";
import { PageHeader, PageFooter } from "@/components/SiteChrome";
import { SERVICES } from "@/data/services";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

const SectionDivider = () => (
  <div className="flex items-center justify-start my-6" aria-hidden>
    <span className="h-px w-10 bg-[#b89461]/50" />
    <span className="mx-3 text-[#b89461] text-xs">✦</span>
    <span className="h-px w-10 bg-[#b89461]/50" />
  </div>
);

export default function Services() {
  const { hash } = useLocation();

  // Scroll to anchor if hash present (e.g. /services#sacred-crown)
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body">
      <PageHeader />

      {/* PAGE HERO */}
      <section className="bg-[#fdfbf7]" data-testid="services-page">
        <div className="mx-auto max-w-4xl px-6 sm:px-10 py-16 md:py-24 text-center">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="font-display text-[11px] tracking-[0.45em] text-[#b89461]"
          >
            OUR SIGNATURE EXPERIENCES
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.1] text-[#2d2926]"
          >
            Sessions Designed
            <span className="block italic text-[#7a5e36]">for Rest &amp; Renewal.</span>
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl mx-auto text-[15px] leading-[1.85] text-[#3f3a35] font-light"
          >
            Every session is paid in full at booking, so your time here stays soft,
            slow, and uninterrupted from the moment you arrive.
          </motion.p>
        </div>

        {/* Quick index */}
        <div className="mx-auto max-w-5xl px-6 sm:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="services-quicknav">
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-sm border border-[#eae0d5] hover:border-[#b89461] bg-[#fdfbf7] hover:bg-[#b89461]/5 transition-all px-4 py-4 text-center"
              >
                <p className="font-display text-[15px] text-[#2d2926] leading-tight">{s.name}</p>
                <p className="mt-2 text-[10px] tracking-[0.28em] uppercase text-[#7a5e36]">
                  {s.duration} · {s.price}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <div className="mx-auto max-w-6xl px-6 sm:px-10 py-16 md:py-24 space-y-24 md:space-y-32">
        {SERVICES.map((s, idx) => (
          <motion.article
            key={s.id}
            id={s.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={fadeUp}
            data-testid={`service-detail-${s.id}`}
            className="grid gap-10 md:gap-16 md:grid-cols-12 items-start scroll-mt-28"
          >
            {/* Image — alternates side */}
            <div className={`md:col-span-5 ${idx % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="overflow-hidden rounded-[1.25rem] shadow-[0_30px_60px_-30px_rgba(122,94,54,0.25)] aspect-[4/5]">
                <img
                  src={s.image}
                  alt={s.name}
                  className="h-full w-full object-cover transition-transform duration-[1500ms] hover:scale-[1.04]"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Copy */}
            <div className={`md:col-span-7 ${idx % 2 === 1 ? "md:order-1" : ""}`}>
              <p className="font-display text-[11px] tracking-[0.45em] text-[#b89461]">
                {`0${idx + 1}`} · {s.duration} · {s.price}
              </p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.4rem] uppercase tracking-tight leading-[1.15] text-[#2d2926]">
                {s.name}
              </h2>
              <SectionDivider />

              <p className="font-display italic text-lg sm:text-xl leading-relaxed text-[#7a5e36]">
                {s.intro}
              </p>
              <p className="mt-5 text-[#3f3a35] text-base leading-[1.95] font-light">
                {s.description}
              </p>
              {s.longDescription && (
                <p className="mt-5 text-[#3f3a35] text-base leading-[1.95] font-light">
                  {s.longDescription}
                </p>
              )}

              {/* Includes */}
              {s.includes?.length > 0 && (
                <div className="mt-8 rounded-sm border border-[#eae0d5] bg-[#f6f1ea]/60 p-6">
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#7a5e36] mb-4">
                    Includes
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {s.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-[14px] text-[#3f3a35] font-light leading-relaxed">
                        <Check className="text-[#b89461] mt-[3px] shrink-0" size={14} strokeWidth={1.5} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {s.closing && (
                <p className="mt-7 font-display italic text-lg text-[#2d2926] leading-relaxed">
                  {s.closing}
                </p>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-5">
                <Link
                  to="/intake"
                  data-testid={`service-book-${s.id}`}
                  className="inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f]"
                >
                  Book {s.name.replace("The ", "")} <ChevronRight size={16} />
                </Link>
                <Link
                  to="/gift-cards"
                  className="text-[11px] tracking-[0.28em] uppercase text-[#7a5e36] hover:text-[#b89461] border-b border-[#7a5e36]/40 hover:border-[#b89461] pb-0.5"
                >
                  Gift this ritual
                </Link>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      {/* Closing CTA strip */}
      <section className="bg-[#f6f1ea]/60 border-t border-[#eae0d5]/70">
        <div className="mx-auto max-w-4xl px-6 sm:px-10 py-20 md:py-24 text-center">
          <p className="font-display italic text-2xl sm:text-3xl text-[#2d2926] leading-relaxed">
            Your hour of quiet is waiting.
          </p>
          <p className="mt-3 text-[11px] tracking-[0.3em] uppercase text-[#b89461]">
            Women only · By appointment · Los Angeles
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/intake"
              data-testid="services-cta-book"
              className="inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] hover:bg-[#a07e4f] transition-all"
            >
              Book Your Session
            </Link>
            <Link
              to="/"
              className="text-[12px] tracking-[0.2em] uppercase text-[#7a5e36] hover:text-[#b89461]"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
